import { type FC, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

import { goReactToGoReactProperties } from '@editor2/mappers/element';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { elementCommonStyles } from '@editor2/styles/element';
import { type GoReactExternalTool } from '@editor2/types/pageElements';

import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import CopyInput from '@editor2/components/CopyInputWrapper';
import CodeMirrorJSON from '@editor2/components/CodeMirrorJSON';
import ScoreSettingsModal from './ScoreSettingsModal';
import ScoreSettingsPreview from './ScoreSettingsPreview';
import { scoreSettingsPreview } from './styles';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';

interface Props {
	element: GoReactExternalTool;
	courseId: number;
}

const GoReact: FC<Props> = (props) => {
	const { element, courseId } = props;

	const commonPayload = { elementId: element.family_id, courseId };
	const initProperties = goReactToGoReactProperties(element);

	const [goReactProperties, setGoreactProperties] = useState({ ...initProperties });
	const [isScoreSettingsModalShown, setScoreSettingsModalShown] = useState(false);

	useEffect(() => {
		const updatedGoReactProperties = goReactToGoReactProperties(element);

		if (JSON.stringify(element) !== JSON.stringify(goReactProperties)) {
			setGoreactProperties({ ...goReactProperties, ...updatedGoReactProperties });
		}
	}, [element]);

	const debouncedDispatch = useDebouncedDispatch();

	const handleChange = (value: Partial<typeof goReactProperties>) => {
		setGoreactProperties({ ...goReactProperties, ...value });
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	return (
		<div className={elementCommonStyles}>
			<CopyInput label="Family ID" value={element.family_id} />

			<Form.Group>
				<Form.Label>Title</Form.Label>
				<Form.Control
					value={goReactProperties.title}
					placeholder="Title"
					onChange={(e) => handleChange({ title: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Prompt body</Form.Label>
				<Form.Control
					value={goReactProperties.prompt_body}
					placeholder="Prompt body"
					onChange={(e) => handleChange({ prompt_body: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Config</Form.Label>
				<CodeMirrorJSON
					value={goReactProperties.config}
					onChange={(value) => handleChange({ config: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Scoring settings preview</Form.Label>
				<div className={scoreSettingsPreview}>
					<ScoreSettingsPreview scoreSettings={goReactProperties.score_config} />

					<Button variant="success" onClick={() => setScoreSettingsModalShown(true)}>
						Update scoring settings
					</Button>
				</div>
			</Form.Group>

			<Form.Group>
				<Form.Label>Caption</Form.Label>
				<CodeMirrorHTML
					value={goReactProperties.caption}
					showTips={false}
					options={{ placeholder: 'External Tool caption' }}
					onChange={(value) => handleChange({ caption: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Credit(s)</Form.Label>
				<CodeMirrorHTML
					value={goReactProperties.credits}
					showTips={false}
					options={{ placeholder: 'External Tool credits' }}
					onChange={(value) => handleChange({ credits: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Transcript URL</Form.Label>
				<Form.Control
					value={goReactProperties.transcript_url}
					placeholder="Transcript URL"
					onChange={(e) => handleChange({ transcript_url: e.target.value })}
				/>
			</Form.Group>

			{isScoreSettingsModalShown && (
				<ScoreSettingsModal
					scoreSettings={goReactProperties.score_config}
					isShown={isScoreSettingsModalShown}
					onHide={() => setScoreSettingsModalShown(false)}
					onChange={handleChange}
				/>
			)}
		</div>
	);
};

export default GoReact;
